// SubmitEventModal.js — Formulaire de soumission d'événement

import React, { useState } from 'react';
import { CATEGORIES, formatYear } from '../data/events';
import { submitEvent } from '../lib/supabase';

export function SubmitEventModal({ user, onClose }) {
  const [step, setStep]             = useState(1);
  const [title, setTitle]           = useState('');
  const [description, setDesc]      = useState('');
  const [year, setYear]             = useState(1000);
  const [category, setCategory]     = useState('POLITICS');
  const [importance, setImportance] = useState(1);
  const [lat, setLat]               = useState('');
  const [lng, setLng]               = useState('');
  const [imageUrl, setImageUrl]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const goStep2 = () => {
    if (!title.trim())       return setError('Le titre est requis');
    if (!description.trim()) return setError('La description est requise');
    setError(''); setStep(2);
  };

  const handleSubmit = async () => {
    setError('');
    if (!lat || !lng) return setError('Les coordonnées sont requises');
    const latN = parseFloat(lat), lngN = parseFloat(lng);
    if (isNaN(latN) || isNaN(lngN)) return setError('Coordonnées invalides');
    if (latN < -90 || latN > 90)    return setError('Latitude entre -90 et 90');
    if (lngN < -180 || lngN > 180)  return setError('Longitude entre -180 et 180');

    setLoading(true);
    const { error: err } = await submitEvent({
      title: title.trim(), description: description.trim(),
      year: Math.round(year), lat: latN, lng: lngN, category, importance,
      image_url: imageUrl.trim() || null,
    }, user);
    setLoading(false);

    if (err) return setError(err.message || 'Erreur lors de la soumission');
    setStep(3);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-large" onClick={e => e.stopPropagation()}>
        <button className="close-btn modal-close" onClick={onClose}>×</button>

        {/* Progress bar */}
        <div className="submit-progress">
          {[{n:1,l:'Informations'},{n:2,l:'Localisation'}].map(({n,l}) => (
            <div key={n} className={`progress-step ${step >= n ? 'active' : ''}`}>
              <div className="progress-num">{n}</div>
              <span className="progress-label">{l}</span>
            </div>
          ))}
          <div className="progress-line" />
        </div>

        {/* ── Step 1 : infos ── */}
        {step === 1 && (
          <>
            <h2 className="modal-title">Soumettre un événement historique</h2>
            <p className="modal-sub">Votre contribution sera vérifiée avant publication</p>

            <div className="modal-fields">
              <div className="modal-field">
                <label>Titre <span className="required">*</span></label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="Ex: Bataille de Poitiers" maxLength={100} autoFocus />
              </div>

              <div className="modal-field">
                <label>Description <span className="required">*</span></label>
                <textarea value={description} onChange={e => setDesc(e.target.value)}
                  placeholder="Décrivez l'événement en quelques phrases..." rows={4} maxLength={600} />
                <span className="field-count">{description.length}/600</span>
              </div>

              <div className="modal-row">
                <div className="modal-field">
                  <label>Année <span className="required">*</span></label>
                  <input type="number" value={year}
                    onChange={e => setYear(parseInt(e.target.value) || 0)}
                    min={-3500} max={2025} />
                  <span className="field-hint">{formatYear(year)}</span>
                </div>

                <div className="modal-field">
                  <label>Importance</label>
                  <div className="importance-picker">
                    {[1,2,3].map(i => (
                      <button key={i} className={`imp-btn ${importance===i?'active':''}`}
                        onClick={() => setImportance(i)}>
                        {'★'.repeat(i)}{'☆'.repeat(3-i)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-field">
                <label>Catégorie <span className="required">*</span></label>
                <div className="category-picker">
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <button key={key}
                      className={`cat-pick-btn ${category===key?'active':''}`}
                      style={{'--cc': cat.color}}
                      onClick={() => setCategory(key)}>
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error && <div className="modal-error">⚠️ {error}</div>}
            <button className="modal-btn" onClick={goStep2}>Suivant → Choisir le lieu</button>
          </>
        )}

        {/* ── Step 2 : localisation ── */}
        {step === 2 && (
          <>
            <h2 className="modal-title">Où s'est passé cet événement ?</h2>
            <p className="modal-sub">Entrez les coordonnées géographiques</p>

            <div className="modal-fields">
              <div className="modal-row">
                <div className="modal-field">
                  <label>Latitude <span className="required">*</span></label>
                  <input type="number" value={lat} onChange={e => setLat(e.target.value)}
                    placeholder="Ex: 48.8566" min={-90} max={90} step="0.0001" />
                </div>
                <div className="modal-field">
                  <label>Longitude <span className="required">*</span></label>
                  <input type="number" value={lng} onChange={e => setLng(e.target.value)}
                    placeholder="Ex: 2.3522" min={-180} max={180} step="0.0001" />
                </div>
              </div>

              <div className="modal-field">
                <label>URL d'une image (optionnel)</label>
                <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)}
                  placeholder="https://upload.wikimedia.org/..." />
                <span className="field-hint">Lien direct vers une image (Wikimedia Commons recommandé)</span>
              </div>

              <div className="coords-help">
                <p>💡 Astuce : cherchez votre lieu sur <a href="https://www.latlong.net" target="_blank" rel="noreferrer">latlong.net</a> pour trouver les coordonnées.</p>
              </div>

              {lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng)) && (
                <div className="coords-preview">
                  📍 {parseFloat(lat).toFixed(4)}°, {parseFloat(lng).toFixed(4)}°
                </div>
              )}
            </div>

            {error && <div className="modal-error">⚠️ {error}</div>}

            <div className="modal-actions">
              <button className="modal-btn modal-btn-secondary" onClick={() => { setStep(1); setError(''); }}>
                ← Retour
              </button>
              <button className="modal-btn" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Envoi en cours…' : '✓ Soumettre l\'événement'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 3 : succès ── */}
        {step === 3 && (
          <div className="modal-confirm">
            <span className="confirm-icon">🎉</span>
            <h3>Événement soumis !</h3>
            <p>Votre événement <strong>"{title}"</strong> est en attente de validation.<br/>Il sera visible sur le globe après approbation.</p>
            <button className="modal-btn" onClick={onClose}>Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}
